import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { getIntegrationImageLogoPath, IntegrationTypes } from '@phoenix/common';
import { CheckboxModule } from 'primeng/checkbox';
import { NotificationService } from '../../../@core/services/notification/notification.service';
import { IntegrationConnectionService } from '../../services/integration-connection.service';
import {
    ProductGroupIntegrationLink,
    ProductGroupIntegrationLinkService,
} from '../../services/product-group-integration-link.service';
import { PhxSelect } from '../phx-select/phx-select.component';

interface ShopCategoryOption {
    id: string;
    label: string;
}

@Component({
    selector: 'app-product-group-integration-row',
    templateUrl: './product-group-integration-row.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, TranslatePipe, CheckboxModule, PhxSelect],
})
export class ProductGroupIntegrationRowComponent implements OnInit {
    private readonly integrationService = inject(IntegrationConnectionService);
    private readonly linkService = inject(ProductGroupIntegrationLinkService);
    private readonly notificationService = inject(NotificationService);

    productGroupId = input.required<string | undefined>();
    link = input.required<ProductGroupIntegrationLink>();
    linkChange = output<ProductGroupIntegrationLink>();

    readonly categoryOptions = signal<ShopCategoryOption[]>([]);
    readonly categoriesSupported = signal(true);
    readonly categoriesLoadFailed = signal(false);
    readonly loadingCategories = signal(false);
    readonly saving = signal(false);

    readonly logoUrl = computed(() =>
        getIntegrationImageLogoPath(this.link().integration?.type as IntegrationTypes),
    );

    readonly shopCategoryCreatedOnSyncHint = _('product-group.shop-category-created-on-sync');

    ngOnInit(): void {
        void this.loadCategories();
    }

    async onCategoryFilter(filter: string): Promise<void> {
        await this.loadCategories(typeof filter === 'string' ? filter : '');
    }

    async onLockShopCategoryChange(locked: boolean): Promise<void> {
        await this.saveMapping({ lockShopCategory: !!locked });
    }

    async onShopCategoryChange(value: string | ShopCategoryOption | null): Promise<void> {
        if (!this.link().lockShopCategory) {
            return;
        }
        const categoryId = this.normalizeSelectValue(value);
        await this.saveMapping({ externalEntityId: categoryId });
    }

    private normalizeSelectValue(value: string | ShopCategoryOption | null): string | null {
        if (value == null || value === '') {
            return null;
        }
        if (typeof value === 'string') {
            return value;
        }
        return value.id ?? null;
    }

    private async loadCategories(search?: string): Promise<void> {
        const integrationId = this.link().integrationId;
        if (!integrationId) {
            return;
        }

        this.loadingCategories.set(true);
        try {
            const mappedId = this.link().externalEntityId?.trim();
            const result = await this.integrationService.getShopCategories(integrationId, {
                search: search?.trim() || undefined,
                ids: mappedId ? [mappedId] : undefined,
            });
            this.categoriesSupported.set(result.supported);
            this.categoriesLoadFailed.set(false);
            this.categoryOptions.set(
                result.items.map(item => ({
                    id: item.id,
                    label: item.breadcrumb?.trim() || item.name,
                })),
            );
        } catch {
            this.categoryOptions.set([]);
            this.categoriesLoadFailed.set(true);
            this.notificationService.error(_('product-group.shop-categories-load-failed'));
        } finally {
            this.loadingCategories.set(false);
        }
    }

    private async saveMapping(
        patch: Pick<ProductGroupIntegrationLink, 'externalEntityId' | 'lockShopCategory'>,
    ): Promise<void> {
        const productGroupId = this.productGroupId();
        const current = this.link();
        if (!productGroupId || !current.integrationId || this.saving()) {
            return;
        }

        this.saving.set(true);
        try {
            const saved = await this.linkService.saveItem({
                id: current.id ?? undefined,
                productGroupId,
                integrationId: current.integrationId,
                externalEntityId: patch.externalEntityId !== undefined
                    ? patch.externalEntityId
                    : current.externalEntityId,
                lockShopCategory: patch.lockShopCategory !== undefined
                    ? patch.lockShopCategory
                    : current.lockShopCategory,
            });
            this.linkChange.emit({
                ...current,
                ...saved,
                integration: saved.integration ?? current.integration,
            });
            this.notificationService.success(_('common.saved'));
        } catch {
            this.notificationService.error(_('common.error-occurred'));
        } finally {
            this.saving.set(false);
        }
    }
}
